import { inject, Injectable, Injector, runInInjectionContext, signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { fetchAndActivate, getBoolean, getString, getValue, RemoteConfig } from '@angular/fire/remote-config';
import { isString, set } from 'lodash';
import { filter, firstValueFrom, take } from 'rxjs';
import { MixpanelService } from '../mix-panel.service/mix-panel.service';

@Injectable({
  providedIn: 'root',
})
export class RemoteConfigService {
  remoteConfig = inject(RemoteConfig);
  mixpanelService = inject(MixpanelService);
  configurations = signal<Map<string, any>>(new Map());
  loading = signal<boolean>(false);
  initialized = signal<boolean>(false);
  injector = inject(Injector);

  constructor() {
    this.setConfigurations();
  }

  /**
   * Sets the configurations from the remote firebase config.
   * @returns {void}
   */
  async setConfigurations() {
    this.loading.set(true);
    try {
      await this.fetchAndActivate();
      runInInjectionContext(this.injector, () => {
        this.configurations.set(
          new Map<string, any>([
            ['ab_test_web_cta_sc_vs_callvet', this.readString('ab_test_web_cta_sc_vs_callvet')],
            ['markerstudy_symptoms_visible', this.readBoolean('markerstudy_symptoms_visible')],
            ['ab_test_optimise_consultation_outcomes', this.readString('ab_test_optimise_consultation_outcomes')],
            [
              'symptom_checker_help_banner_excluded_algo_ids',
              this.readString('symptom_checker_help_banner_excluded_algo_ids'),
            ],
            ['chat_pms', this.readBoolean('chat_pms')],
            ['tenant_ids_without_symptom_checker', this.readString('tenant_ids_without_symptom_checker')],
            ['chat_service_doc_id', this.readString('chat_service_doc_id')],
            ['chat_cta', this.readString('chat_cta')],
            ['chat_cta_afi', this.readString('chat_cta_afi')],
            ['llm_chat_enabled', this.readBoolean('llm_chat_enabled')],
            ['video_from_chat_feature', this.readBoolean('video_from_chat_feature')],
            ['product_to_chat', this.readBoolean('product_to_chat')],
            ['discovery_chat_prompts', this.readString('discovery_chat_prompts')],
            ['video_provider_switch_enabled', this.readBoolean('video_provider_switch_enabled')],
            ['connect_whatsapp_switch', this.readBoolean('connect_whatsapp_switch')],
            ['video_opening_times', this.readValue('video_opening_times')],
          ])
        );
        this.loading.set(false);
      });
    } catch (error) {
      console.error('Error setting configurations', error);
      this.loading.set(false);
    }
  }

  $configurations = toObservable(this.configurations).pipe(
    filter((data) => data?.size > 0),
    take(1)
  );

  /**
   * Fetches and activates the remote firebase config.
   * @returns {Observable<boolean>}
   * Where true is returned when the remote config is fetched
   * and false is return when the cache is used.
   */
  async fetchAndActivate() {
    set(this.remoteConfig, 'settings.minimumFetchIntervalMillis', 0);
    try {
      const remoteConfigInitialised = await this.fetchAndActivateRemoteConfig();
      this.initialized.set(true);
      return remoteConfigInitialised;
    } catch (error) {
      console.error('Error fetching remote config', error);
      return false;
    }
  }

  /**
   * Retrieves the value of a specified experiment configuration and sets it as a super property in Mixpanel.
   *
   * @param name - The name of the experiment configuration to retrieve.
   * @returns The value of the experiment configuration, or undefined if the name is not a string.
   */
  async experiment<T = any>(name: string): Promise<T | undefined> {
    if (!isString(name)) return undefined;
    const config = await firstValueFrom(this.$configurations);
    const experimentValue = config.get(name);
    this.mixpanelService.setSuperProperties({ [`config.${name}`]: experimentValue });
    return experimentValue;
  }

  readString(name: string): string | undefined {
    return getString(this.remoteConfig, name);
  }

  readBoolean(name: string): boolean | undefined {
    return getBoolean(this.remoteConfig, name);
  }

  readValue(name: string): any | undefined {
    return getValue(this.remoteConfig, name).asString();
  }

  fetchAndActivateRemoteConfig(): Promise<boolean> {
    return fetchAndActivate(this.remoteConfig);
  }
}
